import type {
  DbSkill,
  DbSkillTranslation,
  DbProfile,
  DbPublicProfile,
  SkillLocale,
} from "./types";

const KNOWN_LOCALES: SkillLocale[] = [
  "en", "es", "de", "fr", "it", "pt", "ja", "zh", "ru", "hi",
];

function isSkillLocale(v: unknown): v is SkillLocale {
  return typeof v === "string" && (KNOWN_LOCALES as string[]).includes(v);
}

function coerceTranslation(v: unknown): DbSkillTranslation {
  if (!v || typeof v !== "object") return {};
  const o = v as Record<string, unknown>;
  const out: DbSkillTranslation = {};
  if (typeof o.name === "string") out.name = o.name;
  if (typeof o.tagline === "string") out.tagline = o.tagline;
  if (typeof o.description === "string") out.description = o.description;
  if (typeof o.longDescription === "string") out.long_description = o.longDescription;
  else if (typeof o.long_description === "string") out.long_description = o.long_description;
  if (Array.isArray(o.topics)) out.topics = o.topics.filter((x) => typeof x === "string") as string[];
  if (Array.isArray(o.useCases)) out.use_cases = o.useCases.filter((x) => typeof x === "string") as string[];
  else if (Array.isArray(o.use_cases)) out.use_cases = o.use_cases.filter((x) => typeof x === "string") as string[];
  if (typeof o.seoTitle === "string") out.seo_title = o.seoTitle;
  else if (typeof o.seo_title === "string") out.seo_title = o.seo_title;
  if (typeof o.seoDescription === "string") out.seo_description = o.seoDescription;
  else if (typeof o.seo_description === "string") out.seo_description = o.seo_description;
  if (Array.isArray(o.seoKeywords)) out.seo_keywords = o.seoKeywords.filter((x) => typeof x === "string") as string[];
  else if (Array.isArray(o.seo_keywords)) out.seo_keywords = o.seo_keywords.filter((x) => typeof x === "string") as string[];
  return out;
}

function coerceTranslations(v: unknown): Partial<Record<SkillLocale, DbSkillTranslation>> {
  if (!v || typeof v !== "object") return {};
  const o = v as Record<string, unknown>;
  const out: Partial<Record<SkillLocale, DbSkillTranslation>> = {};
  for (const key of Object.keys(o)) {
    if (!isSkillLocale(key)) continue;
    out[key] = coerceTranslation(o[key]);
  }
  return out;
}

function translationToDoc(t: DbSkillTranslation): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  if (t.name !== undefined) out.name = t.name;
  if (t.tagline !== undefined) out.tagline = t.tagline;
  if (t.description !== undefined) out.description = t.description;
  if (t.long_description !== undefined) out.longDescription = t.long_description;
  if (t.topics !== undefined) out.topics = t.topics;
  if (t.use_cases !== undefined) out.useCases = t.use_cases;
  if (t.seo_title !== undefined) out.seoTitle = t.seo_title;
  if (t.seo_description !== undefined) out.seoDescription = t.seo_description;
  if (t.seo_keywords !== undefined) out.seoKeywords = t.seo_keywords;
  return out;
}

// Duck-type check para Firestore Timestamp — evitamos importar `firebase/firestore`
// en este módulo para que `shared/` sea instalable sin deps propias.
function hasToDate(value: unknown): value is { toDate(): Date } {
  return (
    typeof value === "object" &&
    value !== null &&
    "toDate" in value &&
    typeof (value as { toDate?: unknown }).toDate === "function"
  );
}

function tsToIso(value: unknown): string {
  if (!value) return new Date(0).toISOString();
  if (hasToDate(value)) return value.toDate().toISOString();
  if (typeof value === "string") return value;
  if (value instanceof Date) return value.toISOString();
  return new Date(0).toISOString();
}

function tsToIsoOrNull(value: unknown): string | null {
  if (!value) return null;
  if (hasToDate(value)) return value.toDate().toISOString();
  if (typeof value === "string") return value;
  if (value instanceof Date) return value.toISOString();
  return null;
}

type AnyDoc = Record<string, unknown>;

function str(v: unknown): string | null {
  return typeof v === "string" ? v : null;
}
function num(v: unknown, fallback = 0): number {
  return typeof v === "number" ? v : fallback;
}
function bool(v: unknown, fallback = false): boolean {
  return typeof v === "boolean" ? v : fallback;
}
function arr(v: unknown): string[] {
  return Array.isArray(v) ? v.filter((x) => typeof x === "string") : [];
}
function obj(v: unknown): Record<string, unknown> {
  return v && typeof v === "object" ? (v as Record<string, unknown>) : {};
}

export function skillDocToDb(id: string, data: AnyDoc): DbSkill {
  return {
    id,
    slug: (str(data.slug) ?? id) as string,
    name: (str(data.name) ?? str(data.title) ?? id) as string,
    tagline: str(data.tagline),

    icon_url: str(data.iconUrl),
    preview_images: arr(data.previewImages),
    banner_url: str(data.bannerUrl),
    fallback_emoji: str(data.fallbackEmoji),
    fallback_bg_color: str(data.fallbackBgColor),

    category: (str(data.category) ?? "other") as string,
    category_icon: (str(data.categoryIcon) ?? "Package") as string,
    tags: arr(data.tags),
    country: str(data.country),
    compatibility: arr(data.compatibility),
    language: (str(data.language) ?? "EN") as string,

    tier: (str(data.tier) as DbSkill["tier"]) ?? "free",
    badge_type: (str(data.badgeType) as DbSkill["badge_type"]) ?? null,
    price_cents: typeof data.priceCents === "number" ? data.priceCents : null,
    price_monthly_cents:
      typeof data.priceMonthlyCents === "number"
        ? data.priceMonthlyCents
        : null,
    file_url: str(data.fileUrl),
    license: str(data.license),

    description: (str(data.description) ?? "") as string,
    long_description: str(data.longDescription),
    topics: arr(data.topics),
    sources: arr(data.sources),
    use_cases: arr(data.useCases),

    github_url: str(data.githubUrl),
    github_owner: str(data.githubOwner),
    github_repo: str(data.githubRepo),
    github_stars: num(data.githubStars),
    github_forks: num(data.githubForks),
    github_last_updated: tsToIsoOrNull(data.githubLastUpdated),
    install_command: str(data.installCommand),
    npm_package: str(data.npmPackage),
    pypi_package: str(data.pypiPackage),
    version: str(data.version),
    requirements: arr(data.requirements),
    demo_url: str(data.demoUrl),
    readme_url: str(data.readmeUrl),
    mcp_transport: arr(data.mcpTransport),
    mcp_packages: obj(data.mcpPackages),

    certified: bool(data.certified),
    partner: str(data.partner),
    install_count: num(data.installCount),
    rating: num(data.rating),
    rating_count: num(data.ratingCount),

    source_type: str(data.sourceType),
    source_url: str(data.sourceUrl),
    status: (str(data.status) as DbSkill["status"]) ?? "approved",
    submitted_by: str(data.submittedBy),
    reviewed_by: str(data.reviewedBy),
    review_notes: str(data.reviewNotes),
    macro_skill_id: str(data.macroSkillId),
    created_at: tsToIso(data.createdAt),
    published_at: tsToIsoOrNull(data.publishedAt),

    visibility: (str(data.visibility) as DbSkill["visibility"]) ?? "public",
    owner_type: (str(data.ownerType) as DbSkill["owner_type"]) ?? "admin",
    institution_id: str(data.institutionId),
    enterprise_id: str(data.enterpriseId),

    creator_name: str(data.creatorName),
    creator_avatar_url: str(data.creatorAvatarUrl),

    default_locale: (isSkillLocale(data.defaultLocale) ? data.defaultLocale : "en") as SkillLocale,
    translations: coerceTranslations(data.translations),
  };
}

/**
 * Convierte un payload DbSkill (snake_case) a su forma Firestore (camelCase)
 * para escritura. Solo incluye los campos presentes.
 */
export function dbToSkillDoc(
  input: Partial<DbSkill>,
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  const map: Record<string, string> = {
    slug: "slug",
    name: "name",
    tagline: "tagline",
    icon_url: "iconUrl",
    preview_images: "previewImages",
    banner_url: "bannerUrl",
    fallback_emoji: "fallbackEmoji",
    fallback_bg_color: "fallbackBgColor",
    category: "category",
    category_icon: "categoryIcon",
    tags: "tags",
    country: "country",
    compatibility: "compatibility",
    language: "language",
    tier: "tier",
    badge_type: "badgeType",
    price_cents: "priceCents",
    price_monthly_cents: "priceMonthlyCents",
    file_url: "fileUrl",
    license: "license",
    description: "description",
    long_description: "longDescription",
    topics: "topics",
    sources: "sources",
    use_cases: "useCases",
    github_url: "githubUrl",
    github_owner: "githubOwner",
    github_repo: "githubRepo",
    github_stars: "githubStars",
    github_forks: "githubForks",
    install_command: "installCommand",
    npm_package: "npmPackage",
    pypi_package: "pypiPackage",
    version: "version",
    requirements: "requirements",
    demo_url: "demoUrl",
    readme_url: "readmeUrl",
    mcp_transport: "mcpTransport",
    mcp_packages: "mcpPackages",
    certified: "certified",
    partner: "partner",
    status: "status",
    submitted_by: "submittedBy",
    reviewed_by: "reviewedBy",
    review_notes: "reviewNotes",
    macro_skill_id: "macroSkillId",
    source_type: "sourceType",
    source_url: "sourceUrl",
    visibility: "visibility",
    owner_type: "ownerType",
    institution_id: "institutionId",
    enterprise_id: "enterpriseId",
    creator_name: "creatorName",
    creator_avatar_url: "creatorAvatarUrl",
  };
  for (const [snake, camel] of Object.entries(map)) {
    const v = (input as Record<string, unknown>)[snake];
    if (v !== undefined) out[camel] = v;
  }
  // i18n: default_locale + translations
  if (input.default_locale !== undefined) {
    out.defaultLocale = input.default_locale;
  }
  if (input.translations !== undefined) {
    const translationsOut: Record<string, unknown> = {};
    for (const [locale, t] of Object.entries(input.translations ?? {})) {
      if (t) translationsOut[locale] = translationToDoc(t);
    }
    out.translations = translationsOut;
  }
  return out;
}

export function userDocToProfile(uid: string, data: AnyDoc): DbProfile {
  return {
    id: uid,
    email: (str(data.email) ?? "") as string,
    display_name: str(data.displayName),
    avatar_url: str(data.photoURL) ?? str(data.avatarUrl),
    github_username: str(data.githubUsername),
    github_id: str(data.githubId),
    role: (str(data.role) as DbProfile["role"]) ?? "user",
    account_type:
      (str(data.accountType) as DbProfile["account_type"]) ?? "individual",
    verified: bool(data.verified),
    bio: str(data.bio),
    website: str(data.website),
    institution_name: str(data.institutionName),
    stripe_customer_id: str(data.stripeCustomerId),
    stripe_connect_id: str(data.stripeConnectId),
    institution_id: str(data.institutionId),
    enterprise_id: str(data.enterpriseId),
    enterprise_role: (str(data.enterpriseRole) as DbProfile["enterprise_role"]) ?? null,
    seller_type: (str(data.sellerType) as DbProfile["seller_type"]) ?? null,
    newsletter_subscribed: bool(data.newsletterSubscribed),
    newsletter_onboarded: bool(data.newsletterOnboarded),
    newsletter_subscribed_at: tsToIsoOrNull(data.newsletterSubscribedAt),
    created_at: tsToIso(data.createdAt),
    updated_at: tsToIso(data.updatedAt),
  };
}

export function publicProfileDocToDb(
  uid: string,
  data: AnyDoc,
): DbPublicProfile {
  return {
    id: uid,
    display_name: str(data.displayName),
    avatar_url: str(data.photoURL) ?? str(data.avatarUrl),
    github_username: str(data.githubUsername),
    bio: str(data.bio),
    website: str(data.website),
    verified: bool(data.verified),
    account_type:
      (str(data.accountType) as DbPublicProfile["account_type"]) ??
      "individual",
    institution_name: str(data.institutionName),
    created_at: tsToIso(data.createdAt),
    role:
      (str(data.role) as DbPublicProfile["role"]) ?? "user",
  };
}
